import React, { memo } from 'react';
import { MessageCircle } from 'lucide-react';
import JanelaModal from '../../JanelaModal';
import {
  BILLING_MODELS, BILLING_MODEL_LABELS, SCHEDULE_CHAIN, SCHEDULE_BY_ID, SERVICE_TYPES,
  SERVICE_TYPE_BY_ID, tipoDisponivel, semOsOrfaos, cascadeFrom, agendaDaOpu,
  calendarDateOf, datasHerdadas, etapasQuePodeHerdar, MARK,
} from '../../../constants';
import {
  identidadeDoCliente, locaisDoCliente, nomeDoLocal, novoLocal,
} from '../../../data/clients';
import EquipeDaEtapa from '../shared/EquipeDaEtapa';
import { equipeDaEtapa, definirNaEtapa, pessoa } from '../../../utils/equipe';
import { conversaDoWhatsapp } from '../../../data/cliente';
import { VALOR_DO_MODELO, agendaQueFalta, agendaQueSai, validarCadastro } from '../../../data/cadastroDeServico';
import {
  servicosDoCliente, servicosIndistinguiveis,
} from '../../../utils/calculations/duplicataDeServico';
import {
  clientesParecidos, MOTIVO_DA_SEMELHANCA,
} from '../../../utils/calculations/duplicataDeCliente';

// Como cada etapa se apresenta no cadastro. Vocabulário de tela, não de
// agenda — por isso mora aqui e não em SCHEDULE_CHAIN.
const ETAPA_NO_CADASTRO = {
  protocolo: { titulo: 'Protocolo — receptoras', dica: 'Dia do implante. A OPU cai oito dias depois.' },
  opu:       { titulo: 'OPU — coleta de óvulos' },
  fiv:       { titulo: 'FIV — entrada no laboratório' },
  te:        { titulo: 'TE — transferência' },
  dg30:      { titulo: 'DG30 — prenhez aos 30 dias', dica: 'Informativa.' },
  dg60:      { titulo: 'DG60 — prenhez aos 60 dias', dica: 'É esta que define o faturamento no modelo por prenhez.' },
};

const reaisDaEntrada = (n) => Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });

// Placeholder do valor por modelo: exemplo, não sugestão de preço.
const EXEMPLO_DE_VALOR = {
  value_per_te: '500,00', value_per_pregnancy: '1200,00', daily_value: '800,00',
  package_value: '5000,00', value_per_embryo: '300,00',
};

const ServiceModal = memo(({
  showModal,
  modalType,
  selectedItem,
  formData,
  setFormData,
  clients,
  services = [],
  loading,
  onSave,
  onClose,
  // Quem pode cadastrar cliente ganha o atalho dentro do serviço. Nulo para
  // técnico: ele lança serviço, mas não cria cliente.
  onNovoCliente = null,
  // O cadastro de cliente foi aberto de dentro de um serviço: salvar ou
  // cancelar volta para ele, e o botão diz isso.
  voltaAoServico = false,
  // Para ordenar o que já existe do cliente: em andamento primeiro. É o serviço
  // aberto que a pessoa pode estar recadastrando, não o que fechou em março.
  getServiceStatus = null,
  // A taxa de entrada do laboratório, para o exemplo do campo; e a fatura da
  // entrada já emitida deste serviço, que congela a taxa.
  taxaDeEntradaDoLab = 0,
  entradaEmitida = null,
  laboratoryId = null,
}) => {
  if (!showModal) return null;

  const getModalTitle = () => {
    if (modalType === 'client') {
      if (voltaAoServico) return 'Novo cliente para o serviço';
      return selectedItem ? 'Editar Cliente' : 'Novo Cliente';
    }
    return selectedItem ? 'Editar Serviço' : 'Novo Serviço';
  };

  const updateField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const offered = formData.services_offered || [];

  // Repetidas em cada campo; ficam aqui para o JSX dizer o que o campo É, e
  // não como ele se pinta.
  const campo = 'w-full px-4 py-3 text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500';
  const campoCliente = campo;
  const rotulo = 'block text-sm font-medium text-slate-700 mb-2';
  const rotuloOpcional = 'block text-sm font-medium text-slate-500 mb-1';
  const opcional = <span className="text-slate-400 font-normal">(opcional)</span>;
  const secao = 'text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3';

  /**
   * Mexer numa data de etapa empurra todas as seguintes.
   *
   * A árvore é a mesma que o calendário lê (SCHEDULE_CHAIN), então o que se
   * simula aqui é exatamente o que aparece lá. Só desce, e só pelo ramo: mudar
   * a TE não volta para reposicionar a OPU (a exceção é a OPU, que puxa o
   * protocolo ainda não lançado — ver `agendaDaOpu`), e também não mexe nas DGs — elas
   * pendem da FIV, porque a gestação conta da fecundação.
   *
   * Reescreve as datas abaixo mesmo que tenham sido digitadas à mão. É o
   * comportamento previsível — "mudei a OPU, tudo andou junto" — ao custo de
   * ter que reajustar uma etapa que você tinha fixado antes.
   */
  const updateScheduledDate = (stageId, value) => {
    if (!value) {
      updateField(SCHEDULE_BY_ID[stageId].field, value);
      return;
    }
    // A OPU leva junto o protocolo ainda não lançado, para trás: é por ela
    // que se marca, e o início do protocolo é que tem de se encaixar.
    setFormData(prev => ({
      ...prev,
      ...(stageId === 'opu'
        ? agendaDaOpu(value, offered, { protocoloFeito: !!(prev.protocolo_done || prev.protocolo_date) })
        : cascadeFrom(stageId, value, offered)),
    }));
  };

  /**
   * A data do serviço é a cabeça da corrente, e a primeira etapa contratada
   * cai no mesmo dia dela.
   *
   * Aqui só preenche o que está vazio. Mudar a data de um serviço que já tem
   * agenda montada não pode apagá-la em silêncio — quem quiser mover tudo
   * mexe na data da primeira etapa, e a cascata leva as seguintes junto.
   */
  const updateServiceDate = (value) => {
    setFormData(prev => {
      const next = { ...prev, date: value };
      return value ? { ...next, ...agendaQueFalta(next) } : next;
    });
  };

  /**
   * Marcar um tipo já traz a data sugerida da etapa dele — só a que estava em
   * branco. Era o passo que faltava: a data do serviço vem antes dos tipos no
   * formulário, e a cascata a partir dela não tinha tipo nenhum para agendar;
   * o usuário marcava OPU, FIV, TE e via três caixas vazias.
   */
  const handleServiceToggle = (service) => {
    setFormData(prev => {
      const atuais = prev.services_offered || [];
      const marcados = atuais.includes(service)
        ? atuais.filter(s => s !== service)
        : [...atuais, service];

      // Tipo que perdeu a dependencia sai junto: protocolo sem TE e protocolo
      // para ninguem, e ficaria pendente no kanban para sempre.
      const services_offered = semOsOrfaos(marcados);

      return {
        ...prev,
        services_offered,
        ...agendaQueFalta(prev, services_offered),
        // Desmarcou conferência ou protocolo: as datas deles saem da tela na
        // hora. Remarcar sugere de novo, porque o campo voltou a estar vazio.
        ...agendaQueSai(services_offered),
        // A transferência é combinada com o veterinário: o laboratório só cobra
        // a linha da desvitrificação. Só sugere em serviço que ainda não tem
        // modelo, e quem cadastra pode trocar ali mesmo.
        ...(service === 'desvitrificacao' && services_offered.includes(service) && !prev.billing_model
          ? { billing_model: BILLING_MODELS.PERSONALIZADO, entrada_por_embriao: 0 }
          : {}),
        // Contratou a própria OPU: o vínculo com a OPU de outro serviço deixa
        // de fazer sentido e sai junto. Se ficasse, sumiria da tela mas
        // continuaria gravado — e ninguém entenderia de onde veio.
        ...(service === 'opu' && services_offered.includes('opu')
          ? { source_service_id: null, source_stage: null }
          : {}),
      };
    });
  };

  // Quais etapas este serviço pode herdar de outro. A regra mora em
  // constants/schedule.js, com teste: o protocolo precisa ficar FORA da conta,
  // e foi por entrar nela que marcar protocolo zerava a herança.
  const etapasHerdaveis = etapasQuePodeHerdar(offered);

  const etapaHerdada = formData.source_stage || '';

  const outrosServicos = services
    .filter(s => s.id !== formData.id && s.id !== selectedItem?.id);

  /**
   * Serviços que podem ceder a etapa escolhida: os que a contrataram, menos
   * este. Mais recentes primeiro — a coleta que abastece outro serviço costuma
   * ser a de poucos dias atrás.
   */
  const origensPossiveis = !etapaHerdada ? [] : outrosServicos
    .filter(s => (s.services_offered || []).includes(SCHEDULE_BY_ID[etapaHerdada].requires))
    .slice(0, 60);

  const rotuloDoServico = (s, etapa) => {
    const codigo = s.service_code ? `#${s.service_code}` : '(sem número)';
    const lote = s.service_label ? ` · ${s.service_label}` : '';
    const quando = etapa ? calendarDateOf(s, etapa) : null;
    return `${codigo} — ${s.client_name}${lote}${quando ? ` · ${quando}` : ''}`;
  };

  /**
   * Casar uma origem reposiciona a agenda deste serviço.
   *
   * É o ponto do vínculo: herdei a OPU do 2026-0042, então minha FIV é o dia
   * seguinte ao que ELA aconteceu — não uma data que alguém digitou aqui. A
   * data da origem vem por `calendarDateOf`, que prefere a real à agendada:
   * depois de executada, é ela que descreve o que houve.
   *
   * A cascata reescreve as etapas abaixo mesmo que tenham sido digitadas à
   * mão, pelo mesmo motivo que mexer na OPU reescreve: previsibilidade.
   */
  const casarOrigem = (stageId, sourceId) => {
    const origem = services.find(s => s.id === sourceId);
    const data = origem && stageId ? calendarDateOf(origem, stageId) : null;

    setFormData(prev => ({
      ...prev,
      source_stage: stageId || null,
      source_service_id: sourceId || null,
      // A data da própria etapa herdada vem junto. O serviço não executou a
      // FIV, mas precisa saber quando ela foi: é `lab_date` que ancora o prazo
      // da DG60 e as datas previstas por receptora.
      ...(data ? datasHerdadas(origem, stageId) : {}),
      // E daí para baixo, a cascata das etapas que ESTE serviço contrata.
      ...(data ? cascadeFrom(stageId, data, offered) : {}),
    }));
  };

  /**
   * O QUE JÁ EXISTE DAQUELE CLIENTE, mostrado enquanto se preenche.
   *
   * É contexto, não aviso, e é o que de fato previne o recadastro: a pessoa vê que
   * já existe antes de terminar, e abre em vez de criar. Aviso no salvar chega
   * tarde — quem duplica está confiante, e clica.
   *
   * Só ao CRIAR. Editando, a lista dos outros serviços do mesmo cliente é ruído:
   * a pessoa já sabe em qual está.
   *
   * Sem `useMemo` de propósito: este componente retorna antes de qualquer hook, e
   * um hook depois de `return` condicional quebraria a ordem entre renders. São
   * dezenas de serviços — memoizar economizaria nada.
   */
  const jaExistem = (selectedItem || modalType !== 'service')
    ? []
    : servicosDoCliente(formData.client_id, outrosServicos, {
      concluido: (x) => (getServiceStatus ? getServiceStatus(x) === 'Finalizado' : false),
    });

  /**
   * Os serviços de que este ficaria INDISTINGUÍVEL.
   *
   * O aviso não afirma "isto é duplicata", que ele não tem como saber. Afirma o
   * que é verificável: mesmo cliente, mesmas etapas, mesmo modelo e nenhum dos
   * dois com lote — não há nada, em nenhuma tela, que diga qual é qual. A saída
   * que ele oferece é dar um lote, que resolve mesmo se os dois forem legítimos.
   */
  const indistinguiveis = servicosIndistinguiveis(
    { ...formData, id: formData.id || selectedItem?.id }, outrosServicos);

  /**
   * Cadastros de cliente parecidos com este.
   *
   * Mesmo cliente com dois cadastros é pior que serviço duplicado: cada um carrega
   * a sua metade dos serviços, do estoque e do saldo, e nenhuma tela soma as duas.
   *
   * Só no cadastro de CLIENTE, e não ao editar: comparar um cadastro com ele mesmo
   * já é tratado na regra, mas listar semelhantes de quem só está corrigindo um
   * telefone é ruído.
   */
  const parecidos = (modalType === 'client' && !selectedItem)
    ? clientesParecidos(formData, clients)
    : [];

  /**
   * O telefone do cadastro, a um clique da conversa.
   *
   * Sai do que está no CAMPO, e não do que está salvo: quem acabou de corrigir
   * o número quer testar o número novo. Inválido não mostra link nenhum — o
   * silêncio aqui também diz que o que foi digitado não vai servir para chamar
   * ninguém.
   */
  const conversa = conversaDoWhatsapp(formData.phone);

  // Os locais em edição. `openModal` já os entrega prontos — inclusive o do
  // cadastro antigo, lido das colunas soltas; o fallback é só rede.
  const locais = formData.locais?.length ? formData.locais : [novoLocal({ cobranca: true })];
  const mudarLocais = (fn) => setFormData(prev => ({
    ...prev,
    locais: fn(prev.locais?.length ? prev.locais : locais),
  }));
  const adicionarLocal = () => mudarLocais(ls => [...ls, novoLocal()]);
  const mudarLocal = (i, campo, valor) =>
    mudarLocais(ls => ls.map((l, j) => (j === i ? { ...l, [campo]: valor } : l)));
  const marcarCobranca = (i) =>
    mudarLocais(ls => ls.map((l, j) => ({ ...l, cobranca: j === i })));
  // Tirar o de cobrança passa a marca para o primeiro que sobra: o cadastro
  // nunca fica sem endereço de boleto por descuido.
  const removerLocal = (i) => mudarLocais(ls => {
    const resto = ls.filter((_, j) => j !== i);
    return resto.some(l => l.cobranca) ? resto : resto.map((l, j) => ({ ...l, cobranca: j === 0 }));
  });

  // O local do serviço só se pergunta quando há escolha.
  const clienteDoServico = clients.find(c => c.id === formData.client_id);
  const locaisDoServico = locaisDoCliente(clienteDoServico);

  // As etapas com agenda entre as contratadas. VT, DT, compra e diária não
  // estão na corrente: não têm dia marcado.
  const etapasAgendaveis = SCHEDULE_CHAIN.filter(e => offered.includes(e.requires));

  const valorDoModelo = VALOR_DO_MODELO[formData.billing_model];

  // Só a etapa escolhida: as outras ficam como estão, vazias inclusive.
  const mudarEquipe = (etapaId, papel, id, nome) => setFormData(prev => ({
    ...prev,
    equipe_das_etapas: definirNaEtapa(prev.equipe_das_etapas, etapaId, papel, id, nome),
  }));

  // O que ainda falta para gravar. Vazio = pode salvar. A lista aparece no
  // rodapé para o botão desabilitado não ser um mistério. Do cliente só se
  // exige o nome — é o único campo que o banco recusa vazio.
  const faltas = modalType === 'service'
    ? validarCadastro(formData)
    : ((formData.name || '').trim() ? [] : ['o nome do cliente']);
  const podeSalvar = !loading && faltas.length === 0;

  const opcaoDoCliente = (client) => {
    const { principal, secundario } = identidadeDoCliente(client);
    return secundario ? `${principal} — ${secundario}` : principal;
  };

  return (
    <JanelaModal chave="servico" titulo={getModalTitle()} onFechar={onClose} largura="sm:max-w-2xl">

        {/* Corpo do modal (rolável) */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 pt-4">

        {modalType === 'client' ? (
          /* Só o nome é obrigatório. O cadastro nasce no meio de um serviço
             sendo lançado; exigir documento ou endereço ali travaria quem só
             quer registrar a fazenda e seguir. */
          <div className="space-y-4">
            {/* Três nomes, e são três coisas.
                O cadastro tinha um campo só, rotulado "fazenda", e ele virou
                depósito das três: metade dos registros guarda pessoa, a outra
                guarda fazenda, dois guardam as duas juntas entre parênteses, e
                um está com fazenda no campo de contato.

                `name` continua sendo o nome do cliente — quem contrata, e o
                que aparece na fatura. As fazendas ganharam lugar próprio, mais
                abaixo, em Locais. */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Nome do cliente
              </label>
              <input
                type="text"
                placeholder="Quem contrata — pessoa ou empresa"
                className={campoCliente}
                value={formData.name || ''}
                onChange={(e) => updateField('name', e.target.value)}
              />

              {/* ENQUANTO DIGITA, e não no salvar. O nome é o campo pelo qual a
                  duplicata entra, então o lugar do aviso é embaixo dele: quem lê
                  "já existe" com o cursor ainda no campo corrige ali, sem ter
                  preenchido o resto do formulário para descobrir depois.

                  Não bloqueia. Duas fazendas com nomes parecidos existem, e o
                  aviso diz qual é o cadastro e por quê — "já existe um parecido"
                  mandaria a pessoa procurar. */}
              {parecidos.length > 0 && (
                <div className="mt-2 text-xs bg-amber-50 border border-amber-200 rounded p-2.5">
                  <p className="text-slate-700">
                    {MARK.WARN} {MOTIVO_DA_SEMELHANCA[parecidos[0].motivo]}:
                  </p>
                  <ul className="mt-1 space-y-0.5 text-slate-600">
                    {parecidos.slice(0, 3).map(({ client, motivo }) => (
                      <li key={client.id}>
                        <span className="font-medium text-slate-800">{client.name}</span>
                        {client.contact && client.contact !== client.name
                          ? ` · ${client.contact}` : ''}
                        {client.city ? ` · ${client.city}` : ''}
                        {/* O motivo por linha só quando difere do título: repetir
                            "mesmo nome" em três linhas iguais não informa. */}
                        {motivo !== parecidos[0].motivo && (
                          <span className="text-slate-400"> ({MOTIVO_DA_SEMELHANCA[motivo]})</span>
                        )}
                      </li>
                    ))}
                  </ul>
                  {parecidos.length > 3 && (
                    <p className="mt-1 text-slate-500">e outros {parecidos.length - 3}.</p>
                  )}
                  <p className="mt-1.5 text-slate-600">
                    Se for o mesmo, cancele e use o que já existe — dois cadastros do
                    mesmo cliente dividem os serviços e o saldo entre eles.
                  </p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={rotuloOpcional}>Nome do contato</label>
                <input type="text" placeholder="Com quem se fala" className={campoCliente}
                  value={formData.contact || ''}
                  onChange={(e) => updateField('contact', e.target.value)} />
              </div>
              <div>
                <div className="flex items-baseline justify-between gap-2">
                  <label className={rotuloOpcional}>Telefone</label>
                  {conversa && (
                    <a href={conversa} target="_blank" rel="noopener noreferrer"
                      title="Abrir conversa no WhatsApp"
                      className="inline-flex items-center gap-1 mb-1 text-xs text-blue-600 hover:text-blue-800">
                      <MessageCircle size={12} /> WhatsApp
                    </a>
                  )}
                </div>
                <input type="tel" placeholder="(00) 00000-0000" className={campoCliente}
                  value={formData.phone || ''}
                  onChange={(e) => updateField('phone', e.target.value)} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={rotuloOpcional}>Tipo</label>
                <select className={campoCliente}
                  value={formData.client_type || 'pessoa_fisica'}
                  onChange={(e) => updateField('client_type', e.target.value)}>
                  <option value="pessoa_fisica">Pessoa física</option>
                  <option value="pessoa_juridica">Pessoa jurídica</option>
                </select>
              </div>
              <div>
                {/* O rótulo acompanha o tipo: pedir "CPF" a uma empresa é
                    convite a preencher errado. */}
                <label className={rotuloOpcional}>
                  {formData.client_type === 'pessoa_juridica' ? 'CNPJ' : 'CPF'}
                </label>
                <input type="text" className={campoCliente}
                  value={formData.document || ''}
                  onChange={(e) => updateField('document', e.target.value)} />
              </div>
            </div>

            {/* LOCAIS: as fazendas do cliente e qualquer outro endereço dele.
                Era uma fazenda e um endereço só, e quem tinha duas fazendas
                virava dois cadastros (VICTOR VENTURA, RODRIGO ROSA FERREIRA).
                Um dos locais é o de cobrança — pode ser uma das fazendas ou um
                endereço que só existe para o boleto. */}
            <section>
              <div className="flex items-baseline justify-between mb-2">
                <h3 className="text-sm font-medium text-slate-700">Locais</h3>
                <button type="button" onClick={adicionarLocal}
                  className="text-sm text-blue-600 hover:text-blue-800 hover:underline">
                  + Adicionar local
                </button>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Fazendas, sítios ou outro endereço. O marcado com {MARK.COBRANCA} é o
                que vai no boleto.
              </p>

              <div className="space-y-3">
                {locais.map((local, i) => (
                  <div key={local.id} className="border border-slate-200 rounded-lg p-3 space-y-3">
                    <div className="flex items-start gap-2">
                      <div className="flex-1">
                        <label className={rotuloOpcional}>Nome do local</label>
                        <input type="text" placeholder="Ex: Fazenda Santa Rita, Escritório"
                          className={campoCliente}
                          value={local.nome || ''}
                          onChange={(e) => mudarLocal(i, 'nome', e.target.value)} />
                      </div>
                      {locais.length > 1 && (
                        <button type="button" onClick={() => removerLocal(i)}
                          className="mt-6 px-2 py-3 text-sm text-slate-500 hover:text-red-600"
                          title="Remover este local">
                          Remover
                        </button>
                      )}
                    </div>

                    <div>
                      <label className={rotuloOpcional}>Endereço</label>
                      <input type="text" placeholder="Rua, número, bairro — ou estrada, km"
                        className={campoCliente}
                        value={local.endereco || ''}
                        onChange={(e) => mudarLocal(i, 'endereco', e.target.value)} />
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="col-span-2">
                        <label className={rotuloOpcional}>Cidade</label>
                        <input type="text" className={campoCliente}
                          value={local.cidade || ''}
                          onChange={(e) => mudarLocal(i, 'cidade', e.target.value)} />
                      </div>
                      <div>
                        <label className={rotuloOpcional}>UF</label>
                        <input type="text" maxLength={2} placeholder="MG"
                          className={`${campoCliente} uppercase`}
                          value={local.uf || ''}
                          onChange={(e) => mudarLocal(i, 'uf', e.target.value.toUpperCase())} />
                      </div>
                      <div>
                        <label className={rotuloOpcional}>CEP</label>
                        <input type="text" placeholder="00000-000" className={campoCliente}
                          value={local.cep || ''}
                          onChange={(e) => mudarLocal(i, 'cep', e.target.value)} />
                      </div>
                    </div>

                    <div>
                      <label className={rotuloOpcional}>Link de localização</label>
                      <input type="url" placeholder="Cole o link do Google Maps" className={campoCliente}
                        value={local.link || ''}
                        onChange={(e) => mudarLocal(i, 'link', e.target.value)} />
                      {/* Abrir dali mesmo: quem usa isso está indo para a
                          fazenda, e copiar o link para outro app no meio da
                          estrada é o oposto do que ajuda. */}
                      {local.link && (
                        <a href={local.link} target="_blank" rel="noopener noreferrer"
                          className="inline-block mt-1 text-sm text-blue-600 hover:text-blue-800 underline">
                          Abrir localização
                        </a>
                      )}
                    </div>

                    {/* Um só: escolher outro desmarca o anterior. Radio, e não
                        caixa, porque o boleto vai para um endereço. */}
                    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                      <input type="radio" name="local-de-cobranca"
                        checked={!!local.cobranca}
                        onChange={() => marcarCobranca(i)} />
                      {MARK.COBRANCA} Endereço de cobrança
                    </label>
                  </div>
                ))}
              </div>
            </section>
          </div>
        ) : (
          /* O formulário segue a ordem em que se pensa um serviço: para quem e
             quando; o que foi contratado; quando cada etapa acontece; quanto
             custa; e o que mais o contrato diz. Cada bloco só mostra o que os
             anteriores tornaram relevante. */
          <div className="space-y-8">
            {/* ── Para quem e quando ─────────────────────────────────── */}
            <section>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <div className="flex items-baseline justify-between mb-2">
                    <label className="text-sm font-medium text-slate-700">Cliente</label>
                    {/* Cadastrar o cliente sem sair daqui: o cadastro de
                        cliente nasce no meio de um serviço sendo lançado, e
                        ter que cancelar, ir na aba Clientes e voltar era o
                        caminho mais longo do sistema. */}
                    {onNovoCliente && (
                      <button
                        type="button"
                        onClick={onNovoCliente}
                        className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        + Novo cliente
                      </button>
                    )}
                  </div>
                  <select
                    className={campo}
                    value={formData.client_id || ''}
                    onChange={(e) => setFormData(prev => ({
                      // Trocar de cliente esquece o local: era de outro.
                      ...prev, client_id: e.target.value, local_id: null,
                    }))}
                  >
                    <option value="">Selecione o cliente</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>{opcaoDoCliente(client)}</option>
                    ))}
                  </select>

                  {/* EM QUAL FAZENDA. Só aparece quando há escolha: com um
                      local só, ele é o do serviço sem ninguém precisar dizer.
                      Vazio vale o primeiro — é o que a agenda do técnico e o
                      relatório leem (`localDoServico`). */}
                  {locaisDoServico.length > 1 && (
                    <div className="mt-3">
                      <label className={rotulo}>Local</label>
                      <select className={campo}
                        value={formData.local_id || locaisDoServico[0].id}
                        onChange={(e) => updateField('local_id', e.target.value)}>
                        {locaisDoServico.map(l => (
                          <option key={l.id} value={l.id}>
                            {nomeDoLocal(l)}{l.cidade && l.nome ? ` — ${l.cidade}` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* O QUE ESTE CLIENTE JÁ TEM, no instante em que ele é
                      escolhido. Três, e o resto contado: a lista inteira aqui
                      empurraria o formulário para baixo da dobra, e quem tem
                      dezoito serviços não reconhece o seu numa lista de dezoito. */}
                  {jaExistem.length > 0 && (
                    <div className="mt-2 text-xs text-slate-500">
                      <p className="mb-1">
                        Este cliente já tem {jaExistem.length} serviço
                        {jaExistem.length === 1 ? '' : 's'}:
                      </p>
                      <ul className="space-y-0.5">
                        {jaExistem.slice(0, 3).map(x => (
                          <li key={x.id} className="text-slate-600">
                            <span className="font-mono text-slate-500">
                              {x.service_code ? `#${x.service_code}` : '(sem número)'}
                            </span>
                            {x.service_label ? ` · ${x.service_label}` : ''}
                            {x.date ? ` · ${new Date(`${String(x.date).slice(0, 10)}T00:00:00`)
                              .toLocaleDateString('pt-BR')}` : ''}
                          </li>
                        ))}
                      </ul>
                      {jaExistem.length > 3 && (
                        <p className="mt-0.5">e outros {jaExistem.length - 3}.</p>
                      )}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={rotulo}>Data do serviço</label>
                    <input
                      type="date"
                      className={campo}
                      value={formData.date || ''}
                      onChange={(e) => updateServiceDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={rotulo}>Identificador / lote {opcional}</label>
                    <input
                      type="text"
                      placeholder="Ex: Nelore Jan/25, Lote Angus…"
                      className={campo}
                      value={formData.service_label || ''}
                      onChange={(e) => updateField('service_label', e.target.value)}
                      maxLength={60}
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      Aparece no dashboard para diferenciar serviços do mesmo cliente.
                    </p>
                  </div>
                </div>
              </div>

              {/* Número do serviço: só existe depois de gravado, porque quem o
                  gera é o banco. Em serviço novo não há o que mostrar. */}
              {(selectedItem?.service_code || formData.service_code) && (
                <div className="text-sm text-slate-500 mt-3">
                  Número do serviço:{' '}
                  <span className="font-mono text-slate-800">
                    #{selectedItem?.service_code || formData.service_code}
                  </span>
                </div>
              )}
            </section>

            {/* ── O que foi contratado ───────────────────────────────── */}
            <section>
              <h4 className={secao}>Serviços contratados</h4>
              {/* Veio de um clique no calendário: o dia é o da OPU, e o
                  protocolo e a TE se encaixam em volta quando os tipos forem
                  marcados. Dizer isso aqui evita achar que o dia é o início. */}
              {formData.opu_no_dia && !offered.includes('opu') && (
                <p className="text-xs text-slate-500 -mt-1 mb-3">
                  Marque a OPU para ela cair em {formData.opu_no_dia.split('-').reverse().join('/')},
                  o dia escolhido no calendário.
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {SERVICE_TYPES.map(service => {
                  // Desabilitado e explicado, nao escondido: a opcao sumir e
                  // reaparecer ao marcar outra e magica, e nao ensina a regra.
                  const disponivel = tipoDisponivel(service.id, offered);
                  const exigido = SERVICE_TYPE_BY_ID[service.depende]?.label;
                  return (
                    <label
                      key={service.id}
                      className={`flex items-start space-x-3 p-4 border rounded-lg ${
                        disponivel
                          ? 'cursor-pointer hover:bg-slate-50 active:bg-slate-100'
                          : 'opacity-50 cursor-not-allowed bg-slate-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={offered.includes(service.id)}
                        disabled={!disponivel}
                        onChange={() => handleServiceToggle(service.id)}
                        className="form-checkbox h-5 w-5 text-blue-600 mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-base font-medium text-slate-900">{service.label}</p>
                        <p className="text-sm text-slate-500">
                          {disponivel ? service.desc : `Só faz sentido com ${exigido}.`}
                        </p>
                      </div>
                    </label>
                  );
                })}
              </div>

              {/* Herdar etapa de outro serviço. Só aparece quando há etapa
                  anterior à primeira contratada aqui — é exatamente o caso em
                  que a pergunta "de onde vieram os embriões" tem resposta. */}
              {etapasHerdaveis.length > 0 && (
                <div className="mt-4 p-4 border rounded-lg space-y-3">
                  <div>
                    <label className={rotulo}>
                      Herdar etapa de outro serviço {opcional}
                    </label>
                    <select
                      className={campo}
                      value={etapaHerdada}
                      onChange={(e) => casarOrigem(e.target.value, null)}
                    >
                      <option value="">Nenhuma — agenda própria</option>
                      {etapasHerdaveis.map(st => (
                        <option key={st.id} value={st.id}>{st.label} de outro serviço</option>
                      ))}
                    </select>
                  </div>

                  {etapaHerdada && (
                    <div>
                      <label className={rotulo}>Serviço de origem</label>
                      <select
                        className={campo}
                        value={formData.source_service_id || ''}
                        onChange={(e) => casarOrigem(etapaHerdada, e.target.value || null)}
                      >
                        <option value="">Selecione o serviço</option>
                        {origensPossiveis.map(s => (
                          <option key={s.id} value={s.id}>{rotuloDoServico(s, etapaHerdada)}</option>
                        ))}
                      </select>
                      <p className="text-xs text-slate-400 mt-1">
                        Ao casar, a agenda daqui para baixo é refeita a partir da data
                        da {SCHEDULE_BY_ID[etapaHerdada].label} na origem.
                      </p>
                      {origensPossiveis.length === 0 && (
                        <p className="text-xs text-slate-500 mt-1">
                          Nenhum serviço deste laboratório contrata {SCHEDULE_BY_ID[etapaHerdada].label}.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* ── Quando cada etapa acontece ─────────────────────────── */}
            {etapasAgendaveis.length > 0 && (
              <section>
                <h4 className={secao}>Agenda das etapas</h4>
                <p className="text-xs text-slate-500 mb-3">
                  {offered.includes('opu')
                    ? <>Sugeridas a partir da OPU{offered.includes('protocolo') && ': o protocolo começa 8 dias antes'}. Mudar uma data empurra as seguintes; a equipe escolhida numa etapa vai para as seguintes que estiverem vazias.</>
                    : 'Sugeridas a partir da data do serviço. Mudar uma data empurra as seguintes; a equipe escolhida numa etapa vai para as seguintes que estiverem vazias.'}
                </p>

                {/* Uma caixa por etapa contratada, todas iguais. Eram seis
                    blocos escritos à mão, cada um com um layout, e a FIV
                    tinha um campo de observação sem coluna no banco. */}
                <div className="space-y-3">
                  {etapasAgendaveis.map(etapa => {
                    const { titulo, dica } = ETAPA_NO_CADASTRO[etapa.id] || { titulo: etapa.label };
                    return (
                      <div key={etapa.id} className="p-4 border border-slate-200 rounded-lg bg-slate-50">
                        <h5 className="text-sm font-medium text-slate-900 mb-2">{titulo}</h5>
                        <div className={`grid grid-cols-1 gap-3 ${etapa.notesField ? 'sm:grid-cols-2' : ''}`}>
                          <input
                            type="date"
                            aria-label={`Data — ${etapa.label}`}
                            className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded"
                            value={formData[etapa.field] || ''}
                            onChange={(e) => updateScheduledDate(etapa.id, e.target.value)}
                          />
                          {etapa.notesField && (
                            <input
                              type="text"
                              placeholder="Observações…"
                              aria-label={`Observações — ${etapa.label}`}
                              className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded"
                              value={formData[etapa.notesField] || ''}
                              onChange={(e) => updateField(etapa.notesField, e.target.value)}
                            />
                          )}
                        </div>
                        {dica && <p className="text-xs text-slate-500 mt-2">{dica}</p>}
                        {/* Quem vai. Pode ficar em branco: no cadastro nem
                            sempre se sabe — quem conclui a etapa é que tem de
                            dizer. Etapa já lançada mostra, mas o que vale
                            para ela é o técnico gravado na sessão. */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                          <EquipeDaEtapa
                            laboratoryId={laboratoryId}
                            responsavel={pessoa(equipeDaEtapa(formData.equipe_das_etapas, etapa.id), 'responsavel')}
                            ajudante={pessoa(equipeDaEtapa(formData.equipe_das_etapas, etapa.id), 'ajudante')}
                            obrigatorio={false}
                            className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded bg-white"
                            onResponsavel={(id, nome) => mudarEquipe(etapa.id, 'responsavel', id, nome)}
                            onAjudante={(id, nome) => mudarEquipe(etapa.id, 'ajudante', id, nome)}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* ── Quanto custa ───────────────────────────────────────── */}
            <section>
              <h4 className={secao}>Cobrança</h4>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className={rotulo}>Modelo de cobrança</label>
                  <select
                    className={campo}
                    value={formData.billing_model || ''}
                    onChange={(e) => {
                      const modelo = e.target.value;
                      setFormData(prev => ({
                        ...prev,
                        billing_model: modelo,
                        // Voltou a ter cobrança: o ponteiro perde o sentido e sai
                        // junto. Se ficasse, sumiria da tela e continuaria gravado.
                        ...(modelo === BILLING_MODELS.SEM_COBRANCA ? {} : { billing_service_id: null }),
                      }));
                    }}
                  >
                    <option value="">Selecione o modelo</option>
                    {Object.entries(BILLING_MODEL_LABELS).map(([id, label]) => (
                      <option key={id} value={id}>{label}</option>
                    ))}
                  </select>
                </div>

                {/* O valor do modelo escolhido, e só ele. Antes, sem modelo
                    escolhido, apareciam quatro campos de valor de uma vez —
                    e ninguém sabia qual preencher. */}
                {valorDoModelo && (
                  <div>
                    <label className={rotulo}>
                      {valorDoModelo.rotulo} (R$) {valorDoModelo.opcional && opcional}
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder={EXEMPLO_DE_VALOR[valorDoModelo.campo]}
                      className={campo}
                      value={formData[valorDoModelo.campo] || ''}
                      onChange={(e) => updateField(valorDoModelo.campo, e.target.value)}
                    />
                  </div>
                )}

                {/* Onde a cobrança acontece. Opcional: nem sempre há serviço
                    para apontar, e exigir um travaria quem só quer dizer
                    "aqui não se cobra". Preenchido, responde sozinho por que
                    este não foi faturado. */}
                {formData.billing_model === BILLING_MODELS.SEM_COBRANCA && (
                  <div>
                    <label className={rotulo}>Faturado no serviço {opcional}</label>
                    <select
                      className={campo}
                      value={formData.billing_service_id || ''}
                      onChange={(e) => updateField('billing_service_id', e.target.value || null)}
                    >
                      <option value="">Não informado</option>
                      {outrosServicos.slice(0, 60).map(s => (
                        <option key={s.id} value={s.id}>{rotuloDoServico(s)}</option>
                      ))}
                    </select>
                    <p className="text-xs text-slate-400 mt-1">
                      Este serviço não entra na lista de faturamento. Nada é cobrado aqui —
                      nem os custos operacionais.
                    </p>
                  </div>
                )}

                {/* Congelamento: entra POR CIMA do modelo, não no lugar dele.
                    Um serviço por prenhez pode ter vitrificação, e é justamente
                    esse o caso que os criou: o que não virou prenhez foi para o
                    botijão e ocupa nitrogênio. Sem cobrança não tem valor a
                    definir, e aí os campos não aparecem. */}
                {formData.billing_model !== BILLING_MODELS.SEM_COBRANCA && offered.includes('vitrificacao') && (
                  <div>
                    <label className={rotulo}>Valor por embrião vitrificado (R$)</label>
                    <input
                      type="number" min="0" step="0.01" placeholder="250,00"
                      className={campo}
                      value={formData.preco_vitrificacao || ''}
                      onChange={(e) => updateField('preco_vitrificacao', e.target.value)}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Multiplicado pelos vitrificados que a FIV lançar. Soma ao valor do serviço.
                    </p>
                  </div>
                )}

                {formData.billing_model !== BILLING_MODELS.SEM_COBRANCA && offered.includes('dt') && (
                  <div>
                    <label className={rotulo}>Valor por embrião em DT (R$)</label>
                    <input
                      type="number" min="0" step="0.01" placeholder="180,00"
                      className={campo}
                      value={formData.preco_dt || ''}
                      onChange={(e) => updateField('preco_dt', e.target.value)}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Multiplicado pelos congelados em DT que a FIV lançar.
                    </p>
                  </div>
                )}

                {formData.billing_model !== BILLING_MODELS.SEM_COBRANCA && offered.includes('desvitrificacao') && (
                  <div>
                    <label className={rotulo}>Desvitrificação, por embrião de outro laboratório (R$)</label>
                    <input
                      type="number" min="0" step="0.01"
                      className={campo}
                      value={formData.preco_desvitrificacao || ''}
                      onChange={(e) => updateField('preco_desvitrificacao', e.target.value)}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Cobra cada embrião descongelado, mesmo o não transferido. Embrião feito aqui é grátis.
                      {!(parseFloat(formData.preco_desvitrificacao) > 0)
                        && ` ${MARK.WARN} Sem valor, esta linha não é cobrada.`}
                    </p>
                  </div>
                )}

                {/* A ENTRADA: cobrança adiantada por embrião transferido, que
                    sai na TE e é abatida do faturamento final. Só se via e
                    editava dentro da TE; aqui ela é combinada com o resto do
                    contrato. A TE continua lendo deste mesmo campo. Emitida a
                    fatura da entrada, a taxa está no papel e não muda mais. */}
                {formData.billing_model !== BILLING_MODELS.SEM_COBRANCA && offered.includes('te') && (
                  <div>
                    <label className={rotulo}>Entrada por embrião transferido (R$)</label>
                    {entradaEmitida ? (
                      <p className="text-sm text-slate-700">
                        R$ {reaisDaEntrada(formData.entrada_por_embriao ?? taxaDeEntradaDoLab)}
                        <span className="text-slate-500">
                          {' '}· já cobrada na fatura {entradaEmitida.numero || ''}; não muda mais
                        </span>
                      </p>
                    ) : (
                      <>
                        <input
                          type="number" min="0" step="0.01"
                          placeholder={taxaDeEntradaDoLab > 0 ? reaisDaEntrada(taxaDeEntradaDoLab) : '95,00'}
                          className={campo}
                          value={formData.entrada_por_embriao ?? ''}
                          onChange={(e) => updateField('entrada_por_embriao', e.target.value)}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Cobrada na TE, por embrião transferido, e abatida do valor final.
                          {' '}Vazio usa a do laboratório
                          {taxaDeEntradaDoLab > 0 ? ` (R$ ${reaisDaEntrada(taxaDeEntradaDoLab)})` : ''};
                          {' '}zero deixa este serviço sem entrada.
                        </p>
                      </>
                    )}
                  </div>
                )}
              </div>
            </section>

            {/* ── O que mais o contrato diz ──────────────────────────── */}
            <section>
              <h4 className={secao}>Contrato</h4>
              <div className="space-y-4">
                {/* Programa SEBRAE. Ficava no meio dos tipos de serviço, com a
                    mesma cara de checkbox, e parecia o décimo tipo. É condição
                    do contrato, e vive com o contrato. */}
                <label className="flex items-start space-x-3 p-4 border rounded-lg cursor-pointer hover:bg-slate-50 active:bg-slate-100">
                  <input
                    type="checkbox"
                    checked={!!formData.sebrae}
                    onChange={(e) => updateField('sebrae', e.target.checked)}
                    className="form-checkbox h-5 w-5 text-blue-600 mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-medium text-slate-900">SEBRAE</p>
                    <p className="text-sm text-slate-500">Serviço vinculado ao programa.</p>
                  </div>
                </label>

                {/* O que só o programa exige. Fica escondido no serviço
                    particular porque lá não significa nada — e campo sem
                    significado na tela é campo que alguém preenche errado. */}
                {formData.sebrae && (
                  <div className="p-4 border rounded-lg grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Nº do projeto
                      </label>
                      {/* Texto, não número: vem com zeros à esquerda (008902026). */}
                      <input
                        type="text"
                        placeholder="008902026"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={formData.sebrae_projeto || ''}
                        onChange={(e) => updateField('sebrae_projeto', e.target.value)}
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Vai no cabeçalho dos relatórios de visita.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Embriões contratados
                      </label>
                      <input
                        type="number" min="0"
                        placeholder="30"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={formData.embrioes_contratados ?? ''}
                        onChange={(e) => updateField('embrioes_contratados',
                          e.target.value === '' ? null : parseInt(e.target.value))}
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        A meta do contrato, não o preço por embrião.
                      </p>
                    </div>
                  </div>
                )}

                <div>
                  <label className={rotulo}>Observações do contrato {opcional}</label>
                  <textarea
                    rows="3"
                    placeholder="Condições especiais, observações sobre valores, etc."
                    className={campo}
                    value={formData.contract_observations || ''}
                    onChange={(e) => updateField('contract_observations', e.target.value)}
                  />
                </div>
              </div>
            </section>
          </div>
        )}
        </div>

        {/* Footer fixo com botões */}
        <div className="border-t border-slate-200 p-3 sm:p-6 bg-slate-50">
          {/* O que ainda falta, dito antes de tentar. Botão desabilitado sem
              explicação é adivinhação; e o banco respondia "null value in
              column client_name", que ninguém devia ter que ler. */}
          {faltas.length > 0 && (
            <p className="text-xs text-slate-500 mb-2">
              Falta informar {faltas.join(', ')}.
            </p>
          )}

          {/* NÃO BLOQUEIA, E NÃO ACUSA. Dois serviços iguais do mesmo cliente
              existem de verdade — no mesmo dia, em fazendas diferentes. O que o
              aviso diz é o que ele sabe: assim, não vai haver como distinguir os
              dois. E oferece a saída que resolve nos dois casos, o lote. */}
          {modalType === 'service' && indistinguiveis.length > 0 && (
            <div className="text-xs text-slate-700 bg-amber-50 border border-amber-200
                            rounded p-2.5 mb-2">
              <p>
                {MARK.WARN} Sem lote, este serviço fica igual a{' '}
                {indistinguiveis.slice(0, 2).map(x => (x.service_code ? `#${x.service_code}` : 'outro'))
                  .join(' e ')}
                {indistinguiveis.length > 2 ? ` e outros ${indistinguiveis.length - 2}` : ''}
                {' '}— mesmo cliente, mesmas etapas, mesmo modelo de cobrança.
              </p>
              <p className="text-slate-600 mt-1">
                Dá um nome no campo de lote para os dois se distinguirem na tela, ou
                salva assim se forem trabalhos diferentes mesmo.
              </p>
            </div>
          )}
          <div className="flex flex-row gap-2">
            <button
              onClick={onSave}
              disabled={!podeSalvar}
              className="flex-1 bg-blue-600 text-white py-2 px-2 sm:px-3 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-xs sm:text-sm"
            >
              {loading ? 'Salvando...' : 'Salvar'}
            </button>
            <button
              onClick={onClose}
              className="flex-1 bg-slate-500 text-white py-2 px-2 sm:px-3 rounded-md hover:bg-slate-600 font-medium text-xs sm:text-sm"
            >
              {voltaAoServico ? 'Voltar ao serviço' : 'Cancelar'}
            </button>
          </div>
        </div>
    </JanelaModal>
  );
});

export default ServiceModal;
